import { partition } from "es-toolkit";

import { INLINE_TEXT_STYLES } from "@/components/tiptap/inline-text-style/inlineTextStyles";
import {
  ADD_LINK_KEY,
  LINK_VARIABLE_ITEM_PREFIX,
  LINK_VARIABLE_KEY,
  REMOVE_LINK_KEY,
  REMOVE_VARIABLE_LINK_KEY,
} from "@/components/tiptap/tiptap-suggestions/utils";
import { VariableNodeModel } from "@/models/nodes/VariableNodeModel";
import { displayHotkey } from "@/utils/hotkeys";

import { groupSuggestions } from "../utils";

import { createSuggestionFromNode } from "./utils";

import type { SingleSuggestion, SuggestionProvider } from "@/components/suggestions/types";
import type { InlineTextStyle } from "@/components/tiptap/inline-text-style/inlineTextStyles";
import type { DocumentModel } from "@/models/DocumentModel";
import type { SuggestionIntlName } from "@/models/nodes/types";

function getCollapsedMarkSuggestions(doc: DocumentModel): SingleSuggestion<"inlineTextStyle">[][] {
  const rows: SingleSuggestion<"inlineTextStyle">[] = [];
  const { tiptap } = doc.editor;

  if (tiptap?.isActive("link")) {
    rows.push(getLinkSuggestion(true));
  }
  if (tiptap?.isActive("variableLink")) {
    rows.push(getRemoveVariableLinkSuggestion());
  }

  return rows.length > 0 ? [rows] : [];
}

function getRemoveVariableLinkSuggestion(): SingleSuggestion<"inlineTextStyle"> {
  return {
    type: "single",
    item: {
      type: "inlineTextStyle",
      key: REMOVE_VARIABLE_LINK_KEY,
      intlName: "inlineTextStyle.removeVariableLink",
      symbol: "",
    },
    icon: "UnlinkVariable",
    trackingId: "inlineTextStyle.removeVariableLink",
    priority: 1.15,
    popupGroup: "inlineTextStyle",
  };
}

function getLinkToVariableSuggestion(doc: DocumentModel): SingleSuggestion | null {
  const groups = doc.nodeLookup?.groupByKey({
    uniqBy: "labels",
    filter: (_base, type) => type === "variable",
  });
  const children: SingleSuggestion<"variable">[] = [];
  for (const group of groups?.values() ?? []) {
    const [id] = group.ids;
    const node = id ? doc.nodes.get(id) : undefined;
    if (!node?.is(VariableNodeModel) || !node.value) {
      continue;
    }

    const child = createSuggestionFromNode<"variable">(node, { priority: 1 });
    child.item.key = `${LINK_VARIABLE_ITEM_PREFIX}${node.labeledValue}`;
    child.popupGroup = "inlineTextStyle";
    child.trackingId = "inlineTextStyle.linkToVariable";
    children.push(child);
  }

  if (children.length === 0) {
    return null;
  }

  return {
    type: "single",
    item: {
      type: "inlineTextStyle",
      key: LINK_VARIABLE_KEY,
      intlName: "inlineTextStyle.linkToVariable",
      symbol: "",
    },
    icon: "LinkVariable",
    nestedKey: LINK_VARIABLE_KEY,
    nestedItems: groupSuggestions("", children),
    popupGroup: "inlineTextStyle",
    priority: 1.15,
  };
}

function getLinkSuggestion(hasLink: boolean): SingleSuggestion<"inlineTextStyle"> {
  return {
    type: "single",
    item: {
      type: "inlineTextStyle",
      key: hasLink ? REMOVE_LINK_KEY : ADD_LINK_KEY,
      intlName: hasLink ? "inlineTextStyle.removeLink" : "inlineTextStyle.addLink",
      symbol: "",
    },
    icon: hasLink ? "Unlink" : "Link",
    trackingId: hasLink ? "inlineTextStyle.removeLink" : "inlineTextStyle.addLink",
    priority: 1.2,
    popupGroup: "inlineTextStyle",
  };
}

function getStyleSuggestion(
  doc: DocumentModel,
  style: InlineTextStyle,
): SingleSuggestion<"inlineTextStyle"> {
  return {
    type: "single",
    item: {
      type: "inlineTextStyle",
      key: style.key,
      intlName: `inlineTextStyle.${style.key}` as SuggestionIntlName,
      symbol: "",
    },
    icon: style.icon,
    trackingId: `inlineTextStyle.${style.key}`,
    hotkey: style.hotkey ? displayHotkey(style.hotkey) : undefined,
    checked: style.isActive(doc),
    popupGroup: "inlineTextStyle",
    priority: 1,
  };
}

export const inlineTextStyleSuggestionsProvider: SuggestionProvider<"inlineTextStyle"> = {
  type: "inlineTextStyle",
  search: (_str, doc) => {
    const selection = doc.editor.tiptap?.state.selection;
    if (doc.cursor.isActive || !selection || !doc.editor.editorState.isText) {
      return [];
    }

    if (selection.empty) {
      return getCollapsedMarkSuggestions(doc);
    }

    const inlineTextStyleSuggestions = INLINE_TEXT_STYLES.filter(
      (style) => !style.isDisabled(doc),
    ).map((style) => getStyleSuggestion(doc, style));

    if (inlineTextStyleSuggestions.length === 0) {
      return [];
    }

    const [[inlineCodeSuggestion], nestedSuggestions] = partition(
      inlineTextStyleSuggestions,
      (suggestion) => suggestion.item.key === "inlineCode",
    );

    if (inlineCodeSuggestion) {
      inlineCodeSuggestion.priority = 1.1;
    }

    const formatSuggestion: SingleSuggestion<"inlineTextStyle"> = {
      type: "single",
      item: {
        type: "inlineTextStyle",
        key: `${nestedSuggestions[0]?.item.key}`,
        intlName: "inlineTextStyle.format",
        symbol: "",
      },
      icon: "text-style/Format",
      nestedKey: "inlineTextStyle",
      nestedItems: groupSuggestions("", nestedSuggestions),
      popupGroup: "inlineTextStyle",
      priority: 1,
    };

    const suggestions: SingleSuggestion[] = [];
    const { isExpression, isCode, linkHref } = doc.editor.editorState;
    const canLink = !isExpression && !isCode;

    if (canLink) {
      suggestions.push(getLinkSuggestion(!!linkHref));

      const variableLink = doc.editor.tiptap?.isActive("variableLink")
        ? getRemoveVariableLinkSuggestion()
        : getLinkToVariableSuggestion(doc);
      if (variableLink) {
        suggestions.push(variableLink);
      }
    }

    suggestions.push(inlineCodeSuggestion, formatSuggestion);

    return [suggestions];
  },
};
